/**
 * The roles the archetype is about to grant, and the chance to correct them.
 *
 * The dialog's whole argument is that an operator answers "what does this person
 * do here" and does not assemble permissions, so this is shut by default. But it
 * shows the archetype's own grants rather than only additions: a grid that
 * showed nothing while the server granted something would read as "they get none
 * of these", which is worse than no grid. Anyone who can see the matrix can
 * therefore also fix it -- dropping case manager at one clinic but not another is
 * a real arrangement.
 *
 * Editing here does not disable the guarantee underneath. Take the clinician role
 * away everywhere and the request is refused, because that is the invisible
 * clinician this whole flow exists to prevent.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import type { RoleResponse } from '../../../services/api';
import { useStore } from '../../../store';
import { FeatureFlag } from '../../../constants/featureFlags';
import {
  filterNonEmrAgencyRoles,
  filterUserVisibleRoles,
  HIDDEN_CLINIC_ROLE_SLUGS,
} from '../../../utils/rbacRoles';
import { useShowEmrUserManagement } from '../../../hooks/useShowEmrUserManagement';
import {
  AgencyRoleCard,
  ClinicRoleSection,
} from '../../rbac/role-assignment-dialog/rolePickers';
import type { AgencyClinicResponse } from '../types';

interface AdvancedRolesSectionProps {
  clinics: AgencyClinicResponse[];
  /** Every role in the agency, loaded with the dialog so the seed is right. */
  roles: readonly RoleResponse[];
  isLoadingRoles?: boolean;
  /** Agency role ids in force, archetype-derived until edited. */
  agencyRoleIds: string[];
  /** Clinic-scoped role id -> clinic ids in force. */
  clinicRoleIds: Record<string, string[]>;
  /** How many grants the matrix holds, for the closed-state badge. */
  count: number;
  disabled?: boolean;
  /** Open it without being asked, when the roles here are the actual question. */
  openInitially?: boolean;
  /** Why the form cannot be submitted yet, when the reason is in here. */
  problem?: string;
  onToggleAgencyRole: (roleId: string) => void;
  onToggleClinicForRole: (roleId: string, clinicId: string) => void;
  onSelectAllClinicsForRole: (roleId: string, allClinicIds: string[]) => void;
  onClearClinicsForRole: (roleId: string) => void;
}

export const AdvancedRolesSection: React.FC<AdvancedRolesSectionProps> = ({
  clinics,
  roles,
  isLoadingRoles = false,
  agencyRoleIds,
  clinicRoleIds,
  count,
  disabled = false,
  openInitially = false,
  problem,
  onToggleAgencyRole,
  onToggleClinicForRole,
  onSelectAllClinicsForRole,
  onClearClinicsForRole,
}) => {
  const [isOpen, setIsOpen] = useState(openInitially);

  // Opened for them rather than left shut with a message pointing inside it: the
  // roles are the question for this kind of member, so hiding them behind a
  // disclosure asks the operator to go looking for the thing blocking them.
  useEffect(() => {
    if (openInitially) setIsOpen(true);
  }, [openInitially]);

  const { hasFeatureFlag } = useStore(
    useShallow(state => ({ hasFeatureFlag: state.hasFeatureFlag }))
  );
  const showEmr = useShowEmrUserManagement();
  // Clinic roles decide who can be scheduled for a visit, which an agency with
  // no EMR never does, so the tab goes even where the flag is on.
  const showClinicRoles =
    hasFeatureFlag(FeatureFlag.SHOW_CLINIC_INFO_IN_USER_MANAGER) && showEmr;

  const agencyRoles = useMemo(() => {
    // Agency-scoped and non-internal, matching what the incumbent invite
    // dialog offers. Shared rows are not excluded: the agency roles people
    // actually hold are shared, so requiring `agency_id` emptied this list
    // for every agency.
    const visible = filterUserVisibleRoles(
      roles.filter(r => r.scope === 'agency' && r.is_internal !== true)
    );
    return showEmr ? visible : filterNonEmrAgencyRoles(visible);
  }, [roles, showEmr]);

  // `clinic:approver` gates nothing and is hidden from the member grid. There
  // are no existing assignments to preserve for somebody being created, so here
  // it is simply never offered.
  const clinicRoles = useMemo(
    () =>
      roles.filter(
        r => r.scope === 'clinic' && !HIDDEN_CLINIC_ROLE_SLUGS.includes(r.slug)
      ),
    [roles]
  );

  const selectedClinicIdsFor = useCallback(
    (roleId: string) => new Set(clinicRoleIds[roleId] ?? []),
    [clinicRoleIds]
  );

  return (
    <div className='rounded-lg border'>
      <button
        type='button'
        onClick={() => setIsOpen(open => !open)}
        aria-expanded={isOpen}
        className='flex w-full items-center gap-2 rounded-lg px-4 py-3 text-left text-sm font-medium hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      >
        {isOpen ? (
          <ChevronDown className='h-4 w-4 shrink-0' aria-hidden='true' />
        ) : (
          <ChevronRight className='h-4 w-4 shrink-0' aria-hidden='true' />
        )}
        <span>Advanced</span>
        {count > 0 && (
          <Badge variant='secondary' className='whitespace-nowrap text-xs'>
            {count} {count === 1 ? 'role' : 'roles'}
          </Badge>
        )}
        <span className='ml-auto text-xs font-normal text-muted-foreground'>
          The roles this gives them
        </span>
      </button>

      {problem && (
        <p className='flex items-start gap-1.5 px-4 pb-3 text-xs text-amber-800'>
          <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
          <span>{problem}</span>
        </p>
      )}

      {isOpen && (
        <div className='border-t px-4 py-4'>
          {isLoadingRoles ? (
            <div className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Loading roles…
            </div>
          ) : (
            <Tabs defaultValue='agency'>
              <TabsList>
                <TabsTrigger value='agency'>
                  Agency Roles
                  {agencyRoleIds.length > 0 && (
                    <Badge
                      variant='default'
                      className='ml-2 whitespace-nowrap text-xs'
                    >
                      {agencyRoleIds.length}
                    </Badge>
                  )}
                </TabsTrigger>
                {showClinicRoles && (
                  <TabsTrigger value='clinic'>Clinic Roles</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value='agency' className='mt-4'>
                {agencyRoles.length === 0 ? (
                  <p className='text-xs text-muted-foreground'>
                    This agency has no additional roles to choose from.
                  </p>
                ) : (
                  <div className='grid gap-3 sm:grid-cols-2'>
                    {agencyRoles.map(role => (
                      <AgencyRoleCard
                        key={role.id}
                        role={role}
                        selected={agencyRoleIds.includes(role.id)}
                        onToggle={disabled ? () => {} : onToggleAgencyRole}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              {showClinicRoles && (
                <TabsContent value='clinic' className='mt-4'>
                  {clinicRoles.length === 0 ? (
                    <p className='text-xs text-muted-foreground'>
                      This agency has no clinic roles to choose from.
                    </p>
                  ) : (
                    <div className='flex flex-col gap-3'>
                      {clinicRoles.map(role => (
                        <ClinicRoleSection
                          key={role.id}
                          role={role}
                          clinics={clinics}
                          selectedClinicIds={selectedClinicIdsFor(role.id)}
                          onToggleClinic={onToggleClinicForRole}
                          onSelectAll={onSelectAllClinicsForRole}
                          onClear={onClearClinicsForRole}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>
              )}
            </Tabs>
          )}

          <Button
            variant='link'
            size='sm'
            className='mt-3 h-auto p-0 text-xs'
            onClick={() => setIsOpen(false)}
          >
            Done
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdvancedRolesSection;
